import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import styles from './LoginPage.module.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [serverWaking, setServerWaking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (wakingTimerRef.current) clearTimeout(wakingTimerRef.current);
    timerRef.current = null;
    wakingTimerRef.current = null;
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !senha) {
      toast.error('Email e senha são obrigatórios');
      return;
    }

    setIsLoading(true);
    setServerWaking(false);
    setElapsedSeconds(0);

    // Após 5s sem resposta, mostra aviso de "servidor acordando"
    wakingTimerRef.current = setTimeout(() => {
      setServerWaking(true);
      setElapsedSeconds(5);
      timerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }, 5000);

    try {
      await login(email, senha);
      clearTimers();
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error: any) {
      clearTimers();
      setServerWaking(false);
      console.error('Erro no login:', error);

      if (error.response?.status === 401) {
        toast.error('Email ou senha incorretos');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Servidor demorou para responder. Tente novamente.');
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <h1 className={styles.title}>SAGEx</h1>
        <p className={styles.subtitle}>Sistema Expert em Gestão Escolar</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              disabled={isLoading}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="senha">Senha</label>
            <input
              id="senha"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Digite sua senha"
              disabled={isLoading}
              required
              className={styles.input}
            />
          </div>

          <button type="submit" disabled={isLoading} className={styles.button}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        {serverWaking && (
          <div className={styles.wakingBanner}>
            <span className={styles.wakingSpinner} />
            <div>
              <strong>Servidor acordando...</strong>
              <p>O servidor estava em modo de espera. Aguarde mais alguns segundos. ({elapsedSeconds}s)</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
